import {
  DayOfWeek,
  MetricField,
  MetricUnit,
  ReportScheduleRecurrence,
} from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { isTransOrFragmentOrString } from '@/utils/isTransOrFragmentOrString';
import TruncatedTypography from '@components/TruncatedTypography';
import { getScheduleText } from '@features/reports2/utils/schedule';
import { formatDateTimeRangeLabel } from '@features/standardDesign/components/DateTimeRangePicker';
import { Plural, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Divider, Typography } from '@mui/material';
import { grey, teal } from '@mui/material/colors';
import { type FC } from 'react';

interface Props {
  deviceIds: string[];
  metrics: {
    name: MetricField;
    unit: MetricUnit;
  }[];
  timeParameters: {
    period: {
      start: string;
      stop: string | null;
    };
    meanWindow: string | null;
    timezone: string;
  };
  format: {
    createPdf: boolean;
    createExcel: boolean;
    emails: string[];
  };
  enabledSchedule: boolean;
  schedule?: {
    recurrence: ReportScheduleRecurrence;
    dayOfWeek?: DayOfWeek | null;
    daysOfWeek?: DayOfWeek[] | null;
    hours: number;
    minutes: number;
  } | null;
}

export const ReportSummary: FC<Props> = (props) => {
  const { i18n } = useLingui();
  const formatters = useFormatters();

  const content = [
    {
      label: <Trans>Dispositivos y métricas</Trans>,
      val1: {
        content: (
          <Plural value={props.deviceIds.length} one="1 dispositivo" other="# dispositivos" />
        ),
        tooltip: null,
      },
      val2: {
        content: <Plural value={props.metrics.length} one="1 métrica" other="# métricas" />,
        tooltip: null,
      },
      val3: null,
    },
    {
      label: <Trans>Periodos de tiempo</Trans>,
      val1: {
        content: formatDateTimeRangeLabel(
          props.timeParameters.period,
          i18n,
          formatters.formatDateTimeRange,
        ),
        tooltip: null,
      },
      val2: {
        content: props.timeParameters.meanWindow,
        tooltip: null,
      },
      val3: {
        content: props.timeParameters.timezone,
        tooltip: null,
      },
    },
    {
      label: <Trans>Formato y destinatarios</Trans>,
      val1: {
        content: (
          <Box display="flex" alignItems="center" gap={1}>
            {props.format.createPdf ? <CheckCircle color="success" /> : <Cancel color="error" />}
            <Trans>Envío PDF</Trans>
          </Box>
        ),
        tooltip: null,
      },
      val2: {
        content: (
          <Box display="flex" alignItems="center" gap={1}>
            {props.format.createExcel ? <CheckCircle color="success" /> : <Cancel color="error" />}
            <Trans>Envío Excel</Trans>
          </Box>
        ),
        tooltip: null,
      },
      val3: {
        content: (
          <Plural value={props.format.emails.length} one="1 destinatario" other="# destinatarios" />
        ),
        tooltip: null,
      },
    },
    {
      label: <Trans>Programación</Trans>,
      val1: {
        content: (
          <Box
            bgcolor={props.enabledSchedule ? teal[400] : grey[600]}
            py={0.5}
            borderRadius={2}
            width="fit-content"
          >
            <Typography px={4}>
              {props.enabledSchedule ? <Trans>Sí</Trans> : <Trans>No</Trans>}
            </Typography>
          </Box>
        ),
        tooltip: null,
      },
      val23:
        props.enabledSchedule && props.schedule
          ? {
              content: getScheduleText(props.schedule, i18n, formatters),
              tooltip: null,
            }
          : null,
    },
  ];

  return (
    <Box
      display="grid"
      gridTemplateColumns="auto repeat(3, 1fr auto)"
      columnGap={4}
      rowGap={2}
      alignItems="center"
    >
      {content.map((row) => (
        <>
          <Typography
            color="text.secondary"
            px={2}
            pr={4}
            py={1}
            border={1}
            borderColor="divider"
            borderRadius={1}
            mr={3}
          >
            {row.label}
          </Typography>

          {row.val1?.content && isTransOrFragmentOrString(row.val1.content) ? (
            <TruncatedTypography>{row.val1?.content}</TruncatedTypography>
          ) : (
            row.val1?.content ?? <div />
          )}

          <Divider sx={{ borderColor: 'white', height: 28 }} orientation="vertical" />

          {row.val23 ? (
            <>
              {row.val23?.content && isTransOrFragmentOrString(row.val23.content) ? (
                <TruncatedTypography gridColumn="span 3">{row.val23?.content}</TruncatedTypography>
              ) : (
                row.val23?.content ?? <Box gridColumn="span 3" />
              )}
            </>
          ) : (
            <>
              {row.val2?.content && isTransOrFragmentOrString(row.val2.content) ? (
                <TruncatedTypography>{row.val2?.content}</TruncatedTypography>
              ) : (
                row.val2?.content ?? <div />
              )}
              <Divider
                sx={{ borderColor: row.val2?.content ? 'white' : 'transparent', height: 28 }}
                orientation="vertical"
              />
              {row.val3?.content && isTransOrFragmentOrString(row.val3.content) ? (
                <TruncatedTypography>{row.val3?.content}</TruncatedTypography>
              ) : (
                row.val3?.content ?? <div />
              )}
            </>
          )}

          <Divider
            sx={{
              borderColor: row.val3?.content || row.val23?.content ? 'white' : 'transparent',
              height: 28,
            }}
            orientation="vertical"
          />
        </>
      ))}
    </Box>
  );
};
